import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import PageBackground from "@/components/PageBackground";

type CallbackStatus = "processing" | "success" | "error";

const MAX_RETRIES = 5;
const INITIAL_DELAY = 2000; // 2 seconds

export default function SubscriptionCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<CallbackStatus>("processing");
  const [message, setMessage] = useState("Processing your subscription change...");
  const [retryCount, setRetryCount] = useState(0);
  const [canManualRetry, setCanManualRetry] = useState(false);

  const checkSubscription = useCallback(async (subscriptionId: string, attempt: number): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setStatus("error");
      setMessage("Please log in to verify your subscription.");
      return false;
    }

    const { data: subscription, error } = await supabase
      .from("subscriptions")
      .select("plan, provider_subscription_id, expires_at, payment_provider")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching subscription:", error);
      return false;
    }

    // Check if subscription was updated with the expected subscription ID
    if (subscription?.provider_subscription_id === subscriptionId && 
        subscription?.payment_provider === "paypal" &&
        subscription?.plan !== "free") {
      setStatus("success");
      setMessage(`Your subscription has been updated to ${subscription.plan === "premium" ? "Premium" : "Basic"}!`);
      
      toast({
        title: "Subscription Updated!",
        description: `Your plan has been changed to ${subscription.plan === "premium" ? "Premium" : "Basic"}.`,
      });
      return true;
    }

    return false;
  }, [toast]);

  const processCallback = useCallback(async () => {
    const subscriptionId = searchParams.get("subscription_id");
    const baToken = searchParams.get("ba_token");
    
    console.log("Subscription callback params:", { subscriptionId, baToken });

    if (!subscriptionId) {
      setStatus("error");
      setMessage("Invalid callback. Missing subscription information.");
      return;
    }

    // Retry with exponential backoff
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      setRetryCount(attempt + 1);
      const delay = INITIAL_DELAY * Math.pow(1.5, attempt); // Exponential backoff
      
      if (attempt > 0) {
        setMessage(`Verifying subscription... (attempt ${attempt + 1}/${MAX_RETRIES})`);
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        const found = await checkSubscription(subscriptionId, attempt);
        if (found) {
          return;
        }
      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed:`, error);
      }
    }

    // After all retries, show a soft success with manual retry option
    setStatus("success");
    setMessage("Your payment was received. The subscription update is being processed and may take a few moments to appear in your account.");
    setCanManualRetry(true);
    
    toast({
      title: "Payment Received",
      description: "Your subscription is being processed. Please check your account shortly.",
    });
  }, [searchParams, checkSubscription, toast]);

  const handleManualRetry = async () => {
    const subscriptionId = searchParams.get("subscription_id");
    if (!subscriptionId) return;
    
    setStatus("processing");
    setMessage("Checking subscription status...");
    setCanManualRetry(false);
    
    try {
      const found = await checkSubscription(subscriptionId, 0);
      if (!found) {
        setStatus("success");
        setMessage("Your subscription is still being processed. Please check your account page.");
        setCanManualRetry(true);
      }
    } catch (error) {
      console.error("Manual retry failed:", error);
      setStatus("success");
      setMessage("Unable to verify subscription status. Please check your account page.");
      setCanManualRetry(true);
    }
  };

  useEffect(() => {
    processCallback();
  }, [processCallback]);

  return (
    <PageBackground>
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === "processing" && (
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
              )}
              {status === "success" && (
                <CheckCircle className="w-12 h-12 text-green-500" />
              )}
              {status === "error" && (
                <XCircle className="w-12 h-12 text-destructive" />
              )}
            </div>
            <CardTitle>
              {status === "processing" && "Processing..."}
              {status === "success" && "Success!"}
              {status === "error" && "Something went wrong"}
            </CardTitle>
            <CardDescription>{message}</CardDescription>
            {status === "processing" && retryCount > 1 && (
              <p className="text-xs text-muted-foreground mt-2">
                Waiting for payment confirmation...
              </p>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {canManualRetry && (
              <Button variant="outline" onClick={handleManualRetry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Again
              </Button>
            )}
            {status !== "processing" && (
              <>
                <Button onClick={() => navigate("/account")}>
                  Go to Account
                </Button>
                <Button variant="outline" onClick={() => navigate("/home")}>
                  Start Creating Stories
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageBackground>
  );
}
